/* eslint-disable max-lines -- cohesive composer surface; splitting chip rows would spread the contract. */

import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { cn } from '@heidi/utils';
import type { Editor } from '@tiptap/react';
import { getDefaultStore, useAtomValue, useSetAtom, useStore } from 'jotai';
import { ArrowUp, Check, Loader2, Square, X } from 'lucide-react';
import { AnimatePresence, m, useIsPresent, useReducedMotion } from 'motion/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import type {
  ApprovalMode,
  ProjectRunFlags,
  RpaSegmentedTask,
  TrackWorkHomeComposerCta,
  WorkMode,
  WorkSurface,
} from '../../contracts';
import {
  linkedContextItemKey,
  linkedContextItemLabel,
  WORK_HOME_COMPOSER_ENTRY,
} from '../../contracts';
import {
  artifactForLinkedItem,
  composerLinkedItemsAtom,
  composerSkillToastMessages,
  draftsHaveSameContent,
  getProjectsComposerWorkflow,
  handleComposerShellClick,
  hasProjectsComposerSkill,
  isProjectsComposerDraftEmpty,
  isProjectsComposerSubmittable,
  normalizeDraftForMode,
  pendingComposerLinkedContextAtom,
  PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES,
  projectChatComposerMessages,
  setComposerLinkedItemAtom,
  setOpenArtifactAtom,
  stripProjectsComposerSkills,
  useComposerFileDrop,
  useRegisterPendingLinkedContextConsumer,
  useSmartComposerFocus,
  withoutProjectsComposerStarterBundle,
  type Artifact,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerSkillReference,
  type ProjectsComposerStarterBundle,
} from '../../data';

import { composerToolbarIconButtonClassName } from '../../data/projects/components/composer-toolbar-icon-button';
import { type LinkedContextItem } from '../projects/components/LinkContextButton';
import { ComposerAmbientGlow } from '../projects/ComposerAmbientGlow';
import { workLandingComposerGlowClassName } from '../projects/workComposerGlow';
import { useDesktopTargetViewSelection } from '../work-home/use-desktop-target-view-selection';
import { AttachmentRow } from './AttachmentRow';
import type { ComposerAttachmentItem, ComposerAttachments } from './composer-attachments';
import {
  composerAnalyticsSurface,
  composerTestIds,
  type ComposerHost,
} from './composer-host';
import { ComposerFileDropOverlay } from './ComposerFileDropOverlay';
import { composerMotion } from './composerMotion';
import { ProjectApprovalModeSelector } from './ProjectApprovalModeSelector';
import { ProjectRunOptionsSelector } from './ProjectRunOptionsSelector';
import {
  projectRunFlagsWithSpeed,
  type WorkRunSpeed,
} from './ProjectRunOptionsSelector.utils';
import { PromptExpandToggle } from './PromptExpandToggle';
import { RotatingComposerPlaceholder } from './RotatingComposerPlaceholder';
import { useCollapsedEngagement } from './useCollapsedEngagement';
import { useComposerDraft } from './useComposerDraft';
import { usePromptOverflow } from './usePromptOverflow';
import { useRequestEffect } from './useRequestEffect';
import { WorkHomeAttachedContext } from './WorkHomeAttachedContext';

export type {
  ComposerAttachmentItem,
  ComposerAttachments,
  ComposerAttachmentStatus,
  ComposerMergePickedFiles,
} from './composer-attachments';
export type {
  ComposerHost,
  WorkComposerContextLock,
  WorkComposerSessionAppearance,
} from './composer-host';

export type ComposerLayout = 'collapsed' | 'full' | 'condensed';

export type ComposerGlow = 'always' | 'while-generating';

// Both lifted controls share this resting position so they can't drift; z-[5]
// keeps them above the toolbar shell for clicks but below the z-10 drop overlay.
const LIFTED_CONTROL_CLASS = 'absolute bottom-3.5 z-[5]';

/** A Sources copy that is fading out keeps its pixels but not its interactivity. */
const PresenceGuardedSlot = ({ children }: { children: ReactNode }) => {
  const isPresent = useIsPresent();
  return (
    <div
      className={cn('flex', !isPresent && 'pointer-events-none')}
      aria-hidden={isPresent ? undefined : true}
    >
      {children}
    </div>
  );
};

// Collapsed card heights change only through animated values (this padding and
// the toolbar shell), never through a class swap: a docked host anchors the card
// by its bottom edge, so any instant height step shows as a jump. The editor row
// is one 29px text line in both states (the 36px inline controls overhang it),
// so 10.5px keeps the resting bar at 52px.
const COMPACT_CARD_PADDING_Y = 10.5;
const EXPANDED_CARD_PADDING_Y = 12;

export interface ComposerPromptEditorHandle {
  focus(position?: 'start' | 'end'): void;
  getEditor(): Editor | null;
  replaceText(text: string): void;
  replaceDraft(draft: ProjectsComposerPromptDraft): void;
  insertSkill(skill: ProjectsComposerSkillReference): void;
  insertChatObject(item: LinkedContextItem): void;
  clear(): void;
}

export interface ComposerPromptEditorBind {
  editorRef: RefObject<ComposerPromptEditorHandle | null>;
  anchorRef: RefObject<HTMLDivElement | null>;
  availableSkills: readonly ProjectsComposerSkillReference[];
  autoFocus: boolean;
  placeholder: string;
  disabled: boolean;
  prefilledValue?: string;
  prefilledSkill?: ProjectsComposerSkillReference;
  /** Visible-only stand-in for the placeholder while a starter is hovered. */
  placeholderPreview?: string | null;
  /** Visible-only node rendered in the editor-owned placeholder overlay slot. */
  placeholderOverlay?: ReactNode;
  onDraftChange: (draft: ProjectsComposerPromptDraft) => void;
  onSubmitRequest: (draft?: ProjectsComposerPromptDraft) => void;
  /** Sends a picked shortcut without using the typed draft as the message. */
  onShortcutSend?: (query: string) => void;
  onLinkContext: (item: LinkedContextItem | null) => void;
  onOpenArtifact: (artifact: Artifact | null) => void;
  /** False outside Work's out-of-session composer: `@` keeps main's chip pick. */
  objectTokens: boolean;
  onClipboardPaste?: (event: ClipboardEvent) => void;
  onEditorChange: (editor: Editor | null) => void;
  analyticsSurface: WorkSurface | undefined;
  className: string;
  editorClassName: string;
  testId: string;
}

/**
 * What a trailing-slot renderer needs.
 *
 * The composer owns its editor instance, so a sheet that hangs off the card —
 * the inline suggestion typeahead — cannot be a plain `ReactNode`: it has to be
 * handed the editor the clinician is typing into.
 */
export interface ComposerCardTrailingBind {
  editor: Editor | null;
  /** Reports the trailing surface's actual rendered visibility back to the card. */
  onVisibilityChange: (visible: boolean) => void;
}

export interface ComposerDictateSlotBind {
  editor: Editor | null;
  getEditor: () => Editor | null;
  disabled: boolean;
  buttonClassName: string;
  onDictateClick: () => void;
}

/**
 * What the live-dictation row needs. The host owns the dictation store, so it
 * renders the row (cancel / waveform / submit + status) and reports back
 * whether a dictation is live; while it is, the card hides the editor and the
 * toolbar and lets the row take the editor's place. The editor and the
 * dictate slot stay mounted underneath so the transcript still lands in the
 * prompt when the dictation completes.
 */
export interface ComposerDictationBind {
  editor: Editor | null;
  getEditor: () => Editor | null;
  onActiveChange: (active: boolean) => void;
}

export interface ComposerAddMenuBind {
  items: readonly LinkedContextItem[];
  onSet: (item: LinkedContextItem | null) => void;
  /** Work mode: a `+` session/patient pick becomes an inline token, not a chip. */
  onPickObject?: (item: LinkedContextItem) => void;
  onAfterPick: () => void;
  onAttachFiles: () => void;
  skillsMenu?: {
    skills: readonly ProjectsComposerSkillReference[];
    onInvoke: (skill: ProjectsComposerSkillReference) => void;
  };
  speedMenu?: {
    value: WorkRunSpeed;
    onChange: (next: WorkRunSpeed) => void;
  };
  modeMenu?: {
    value: WorkMode;
    onChange: (next: WorkMode) => void;
  };
  disabled: boolean;
  analyticsSurface: WorkSurface | undefined;
  /** Sources row in `+`: condensed without a toolbar Sources control, or collapsed while idle. */
  showSourcesInAddMenu: boolean;
  /** Picked shortcut from the add menu; absent when sending is blocked. */
  onShortcutSend?: (query: string) => void;
}

export interface ComposerSubmitPayload {
  draft: ProjectsComposerPromptDraft;
  attachments: readonly ComposerAttachmentItem[];
  linkedContext: readonly LinkedContextItem[];
  runFlags?: ProjectRunFlags;
  approvalMode?: ApprovalMode;
}

export interface WorkCreateCommand {
  text: string;
  displayText: string;
  attachments: readonly ComposerAttachmentItem[];
  linkedContext: readonly LinkedContextItem[];
  runFlags?: ProjectRunFlags;
  computerUseSkill?: RpaSegmentedTask;
  approvalMode?: ApprovalMode;
  /** Analytics-only `entry_point` for the created project's first turn; never control flow. */
  entryPoint?: string;
  /** Draft still in the box when a shortcut sent; the host restores it in the composer it opens. */
  keptDraft?: ProjectsComposerPromptDraft;
}

export type WorkCreateSubmit = (
  payload: ComposerSubmitPayload,
  prepareCreate?: (command: WorkCreateCommand) => WorkCreateCommand | null
) => void;

export interface ComposerStopState {
  ariaLabel: string;
  disabled: boolean;
  pending: boolean;
  onStop: () => void;
}

export interface ComposerProps {
  host: ComposerHost;
  attachments: ComposerAttachments;
  onSubmit: (payload: ComposerSubmitPayload) => void;
  /** Sends a picked shortcut as its own message; second arg is the draft still in the box. */
  onShortcutSubmit?: (
    payload: ComposerSubmitPayload,
    keptDraft: ProjectsComposerPromptDraft
  ) => void;
  isSubmitting?: boolean;
  /** Whether a turn is generating; defaults to submission state for glow visibility. */
  isGenerating?: boolean;
  disabled?: boolean;
  className?: string;
  prefilledValue?: string;
  prefilledSkill?: ProjectsComposerSkillReference;
  showRunOptions?: boolean;
  showSpeed?: boolean;
  /** Speed last picked on another Work composer. ``null`` when they never have. */
  runSpeed?: WorkRunSpeed | null;
  onRunSpeedChange?: (next: WorkRunSpeed) => void;
  mode?: WorkMode;
  onModeChange?: (next: WorkMode) => void;
  chatObjectRefsEnabled?: boolean;
  /** Opts this specific Work-home landing composer into the pending token hand-off. */
  consumesPendingLinkedContext?: boolean;
  /** Reports picker/nav tokens after they have been inserted into the editor. */
  onObjectTokenInserted?: (item: LinkedContextItem) => void;
  /** Mode the user last picked elsewhere; null when they never have. */
  approvalMode?: ApprovalMode | null;
  onApprovalModeChange?: (next: ApprovalMode) => void;
  voiceTrigger?: ReactNode;
  layout: ComposerLayout;
  /** When false, drops chat elevation so a stacked panel below is not double-shadowed. */
  elevated?: boolean;
  glow?: ComposerGlow;
  /** Collapsed layouts stay idle until focus. Pass true to open focused. */
  autoFocus?: boolean;
  /** Increments to focus an already-mounted collapsed composer. autoFocus runs once. */
  focusRequest?: number;
  /**
   * Increments to settle an expanded collapsed-layout composer. The mirror of
   * `focusRequest`, for a panel below the composer whose own dismiss ends the
   * engagement it was holding open.
   */
  collapseRequest?: number;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Fires when a live dictation (see {@link ComposerDictationBind}) starts or
   * ends, so a host can clear surfaces below the card while the clinician
   * speaks.
   */
  onDictationActiveChange?: (active: boolean) => void;
  placeholder?: string;
  /** Shown over the placeholder while the prompt is empty; never names the textbox. */
  placeholderPreview?: string | null;
  /** Host-supported idle hints. Undefined leaves the normal placeholder visible. */
  rotatingPlaceholder?: readonly MessageDescriptor[];
  managedStarterBundle?: ProjectsComposerStarterBundle | null;
  draftValue: ProjectsComposerPromptDraft;
  onDraftChange: (draft: ProjectsComposerPromptDraft) => void;
  contextCardSlot?: ReactNode;
  onLinkedContextChange?: (item: LinkedContextItem | null) => void;
  renderPromptEditor: (bind: ComposerPromptEditorBind) => ReactNode;
  promptEditorRef?: RefObject<ComposerPromptEditorHandle | null>;
  dictateSlot?: (bind: ComposerDictateSlotBind) => ReactNode;
  /** Live-dictation row rendered in the editor's place — see {@link ComposerDictationBind}. */
  renderDictation?: (bind: ComposerDictationBind) => ReactNode;
  sourcesSlot?: ReactNode;
  linkedContextBadge?: (
    item: LinkedContextItem,
    bind: { onOpen?: () => void; onRemove: () => void }
  ) => ReactNode;
  renderAddMenu: (bind: ComposerAddMenuBind) => ReactNode;
  availableSkills?: readonly ProjectsComposerSkillReference[];
  isDesktopBridgeAvailable?: boolean;
  trackCta?: TrackWorkHomeComposerCta;
  onClipboardPaste?: (
    event: ClipboardEvent,
    attach: (files: FileList | readonly File[] | null) => void
  ) => void;
  /**
   * When true, the composer stops owning file drop: no card drag handlers and no
   * internal drop overlay. The host renders a full-surface takeover instead (Work
   * home, full-screen and docked project chat) so a drag never shows two layers.
   */
  disableFileDrop?: boolean;
  /** Grey Send only — the box stays typed. */
  sendDisabled?: boolean;
  sendBlocked?: boolean;
  persistentSend?: boolean;
  composerDockLeading?: ReactNode;
  desktopTargetSlot?: ReactNode;
  bannerSlot?: ReactNode;
  promptOverflow?: { expanded: boolean; onToggle?: () => void };
  disabledMessage?: ReactNode;
  squareBottomEdge?: boolean;
  cardTrailingSlot?: ReactNode;
  /** Trailing slot that needs the live editor — see {@link ComposerCardTrailingBind}. */
  renderCardTrailing?: (bind: ComposerCardTrailingBind) => ReactNode;
  toolbarLeadingSlot?: ReactNode;
  /**
   * Standalone Sources control. Required unless `layout` is `condensed`,
   * where Sources falls back to the + menu when this is omitted. In collapsed,
   * it lives in the menu until the composer expands. Omitting it on the full
   * layout does not fall back.
   */
  toolbarSourcesSlot?: ReactNode;
  /**
   * Collapsed only: also show `toolbarSourcesSlot` on the idle row (instead of
   * the + menu row). It slides into its toolbar slot when the composer expands.
   */
  showToolbarSourcesWhileIdle?: boolean;
  sendAriaLabel?: string;
  sendSucceeded?: boolean;
  stopState?: ComposerStopState;
}

const NO_SKILLS: readonly ProjectsComposerSkillReference[] = [];

const WorkComposerSourcesSlotContext = createContext<ReactNode>(null);

/** Page-owned Sources control for every non-condensed composer under this route. */
export const WorkComposerSourcesSlotProvider = ({
  value,
  children,
}: {
  value: ReactNode;
  children: ReactNode;
}): JSX.Element => (
  <WorkComposerSourcesSlotContext.Provider value={value}>
    {children}
  </WorkComposerSourcesSlotContext.Provider>
);

export const useWorkComposerSourcesSlot = (): ReactNode =>
  useContext(WorkComposerSourcesSlotContext);

interface PendingLinkedContextConsumerProps {
  editor: Editor | null;
  editorRef: RefObject<ComposerPromptEditorHandle | null>;
  objectTokensEnabled: boolean;
  onObjectTokenInserted?: (item: LinkedContextItem) => void;
  setOpenArtifact: (artifact: Artifact) => void;
}

const PendingLinkedContextConsumer = ({
  editor,
  editorRef,
  objectTokensEnabled,
  onObjectTokenInserted,
  setOpenArtifact,
}: PendingLinkedContextConsumerProps): null => {
  const pendingLinkedContext = useAtomValue(pendingComposerLinkedContextAtom);
  const store = useStore();

  useRegisterPendingLinkedContextConsumer();

  useEffect(() => {
    if (!editor || !objectTokensEnabled || pendingLinkedContext?.prefer !== 'token')
      return;

    // Claim against the live store, not the render snapshot. If two opted-in
    // composers commit together, only the first effect can clear and act.
    const claimed = store.get(pendingComposerLinkedContextAtom);
    if (claimed?.prefer !== 'token') return;
    store.set(pendingComposerLinkedContextAtom, null);

    const isExpired = claimed.expiresAt !== undefined && claimed.expiresAt <= Date.now();
    if (claimed.destination !== 'work-home' || isExpired) return;

    const editorHandle = editorRef.current;
    if (editorHandle) {
      editorHandle.insertChatObject(claimed.item);
      onObjectTokenInserted?.(claimed.item);
    }

    if (!claimed.openArtifact) return;
    const artifact = artifactForLinkedItem(claimed.item);
    if (artifact) setOpenArtifact(artifact);
  }, [
    editor,
    editorRef,
    objectTokensEnabled,
    onObjectTokenInserted,
    pendingLinkedContext,
    setOpenArtifact,
    store,
  ]);

  return null;
};

export const Composer = ({
  host,
  attachments,
  onSubmit,
  onShortcutSubmit,
  isSubmitting = false,
  isGenerating = isSubmitting,
  disabled = false,
  className,
  prefilledValue,
  prefilledSkill,
  showRunOptions = false,
  showSpeed = false,
  runSpeed: rememberedSpeed = null,
  onRunSpeedChange,
  mode = 'work',
  onModeChange,
  chatObjectRefsEnabled = false,
  consumesPendingLinkedContext = false,
  onObjectTokenInserted,
  voiceTrigger,
  layout,
  elevated = true,
  glow,
  autoFocus = false,
  focusRequest,
  collapseRequest,
  onExpandedChange,
  onDictationActiveChange,
  placeholder,
  placeholderPreview,
  rotatingPlaceholder,
  managedStarterBundle,
  draftValue,
  onDraftChange,
  contextCardSlot,
  onLinkedContextChange,
  renderPromptEditor,
  promptEditorRef,
  dictateSlot,
  renderDictation,
  sourcesSlot,
  linkedContextBadge,
  renderAddMenu,
  availableSkills = NO_SKILLS,
  isDesktopBridgeAvailable = false,
  trackCta,
  approvalMode: rememberedApprovalMode = null,
  onApprovalModeChange,
  onClipboardPaste,
  disableFileDrop = false,
  sendDisabled = false,
  sendBlocked = false,
  persistentSend = false,
  composerDockLeading,
  desktopTargetSlot,
  bannerSlot,
  promptOverflow,
  disabledMessage,
  squareBottomEdge = false,
  cardTrailingSlot,
  renderCardTrailing,
  toolbarLeadingSlot,
  toolbarSourcesSlot,
  showToolbarSourcesWhileIdle = false,
  sendAriaLabel,
  sendSucceeded = false,
  stopState,
}: ComposerProps): JSX.Element => {
  const analyticsSurface = composerAnalyticsSurface(host);
  const testIds = composerTestIds(host);
  const offeredSkills = mode === 'evidence' ? NO_SKILLS : availableSkills;
  const routinesOffered = mode !== 'evidence';
  const attachedRoutineId = routinesOffered
    ? (getProjectsComposerWorkflow(draftValue)?.skillId ?? null)
    : null;
  const setAttachedRoutineId = useDesktopTargetViewSelection()?.setAttachedRoutineId;
  useEffect(() => {
    if (!setAttachedRoutineId) return undefined;
    setAttachedRoutineId(attachedRoutineId);
    return () => setAttachedRoutineId(null);
  }, [attachedRoutineId, setAttachedRoutineId]);
  // Tokens are Work-mode, out-of-session only; everywhere else runs main's chips.
  const objectTokensEnabled =
    chatObjectRefsEnabled && mode === 'work' && !(host.kind === 'project' && host.lock);
  const prefilledDesktopSkill =
    isDesktopBridgeAvailable || prefilledSkill?.kind === 'workflow'
      ? prefilledSkill
      : undefined;
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const isBoundChat = host.kind === 'project';
  const setLinkedItem = useSetAtom(setComposerLinkedItemAtom);
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  // Single-slot removal: clearing the chip also closes its artifact on
  // the canvas so the surface snaps back to the default state.
  const removeLinkedItem = () => {
    setLinkedItem(null);
    setOpenArtifact(null);
    onLinkedContextChange?.(null);
  };
  const ownEditorRef = useRef<ComposerPromptEditorHandle | null>(null);
  const editorRef = promptEditorRef ?? ownEditorRef;
  const [tiptapEditor, setTiptapEditor] = useState<Editor | null>(null);
  const [isCardTrailingVisible, setIsCardTrailingVisible] = useState(false);
  const hasRenderedCardTrailing = renderCardTrailing !== undefined;
  useEffect(() => {
    if (!hasRenderedCardTrailing) setIsCardTrailingVisible(false);
  }, [hasRenderedCardTrailing]);
  const [isDictationActive, setIsDictationActive] = useState(false);
  const hasRenderedDictation = renderDictation !== undefined;
  useEffect(() => {
    if (!hasRenderedDictation) setIsDictationActive(false);
  }, [hasRenderedDictation]);
  useEffect(() => {
    onDictationActiveChange?.(isDictationActive);
  }, [isDictationActive, onDictationActiveChange]);
  // The collapsed toolbar shell animates between 0 and this row's own height,
  // rather than the string `'auto'`: a dictation ending un-hides the row in the
  // same commit that the transcript lands (sometimes via a DOM mutation outside
  // React), and framer-motion resolves `'auto'` by measuring on a later frame —
  // a moment that isn't guaranteed to land after every one of those updates has
  // painted. A continuously-tracked pixel value sidesteps that measurement
  // entirely. Readings of 0 (the row is `hidden` mid-dictation) are ignored so
  // the shell keeps animating to the last known-good height instead of
  // restarting from scratch.
  const [toolbarContentNode, setToolbarContentNode] = useState<HTMLDivElement | null>(
    null
  );
  const [toolbarContentHeight, setToolbarContentHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!toolbarContentNode) return;
    const measure = () => {
      const height = toolbarContentNode.offsetHeight;
      if (height <= 0) return;
      setToolbarContentHeight((current) => (current === height ? current : height));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(toolbarContentNode);
    return () => observer.disconnect();
  }, [toolbarContentNode]);
  // Null = never picked here and none remembered; the submit sends no mode at all.
  const [pickedApprovalMode, setPickedApprovalMode] = useState<ApprovalMode | null>(null);
  const approvalMode = isBoundChat
    ? rememberedApprovalMode
    : (pickedApprovalMode ?? rememberedApprovalMode);
  const handleApprovalModeChange = (next: ApprovalMode) => {
    trackCta?.(WORK_HOME_COMPOSER_ENTRY.approvalMode(next), {
      surface: analyticsSurface,
    });
    if (!isBoundChat) setPickedApprovalMode(next);
    onApprovalModeChange?.(next);
  };
  const [runFlags, setRunFlags] = useState<ProjectRunFlags>({});
  const [pickedSpeed, setPickedSpeed] = useState<WorkRunSpeed | null>(null);
  const speed: WorkRunSpeed = pickedSpeed ?? rememberedSpeed ?? 'normal';
  const canApplySpeed = showSpeed && mode !== 'evidence';
  const handleSpeedChange = (next: WorkRunSpeed) => {
    setPickedSpeed(next);
    setRunFlags((current) => projectRunFlagsWithSpeed(current, next));
    onRunSpeedChange?.(next);
  };
  const speedMenu = canApplySpeed
    ? {
        value: speed,
        onChange: handleSpeedChange,
      }
    : undefined;
  const shouldReduceMotion = useReducedMotion();
  const motionConfig = composerMotion(shouldReduceMotion);
  const intl = useIntl();
  const isCollapsibleLayout = layout === 'collapsed';

  const focusComposerRaw = useCallback(() => {
    queueMicrotask(() => editorRef.current?.focus('end'));
  }, [editorRef]);
  const { focusComposerAlways } = useSmartComposerFocus({ focus: focusComposerRaw });

  const attachmentsEnabled = !disabled && !isSubmitting;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentItems = attachments.items;
  const attachFiles = useCallback(
    (files: FileList | readonly File[] | null) => {
      attachments.attach(files);
      focusComposerAlways();
    },
    [attachments, focusComposerAlways]
  );
  const handlePaste = (event: ClipboardEvent) => {
    if (!attachmentsEnabled) return;
    onClipboardPaste?.(event, attachFiles);
    if (event.defaultPrevented) return;
    const files = event.clipboardData?.files;
    if (files && files.length > 0) {
      event.preventDefault();
      attachFiles(files);
    }
  };
  const { isDragOver, ...dropHandlers } = useComposerFileDrop({
    enabled: attachmentsEnabled && !disableFileDrop,
    onFilesDropped: attachFiles,
  });

  const { draftRef, handleDraftChange } = useComposerDraft({
    draftValue,
    managedStarterBundle,
    editorRef,
    editor: tiptapEditor,
    onDraftChange,
    onDraftSettled: (next, previous) => {
      if (
        mode === 'evidence' &&
        hasProjectsComposerSkill(next) &&
        !hasProjectsComposerSkill(previous)
      ) {
        onModeChange?.('work');
      }
    },
  });

  const draftHasSkill = hasProjectsComposerSkill(draftValue);
  const isPromptSubmittable = isProjectsComposerSubmittable(
    draftValue.text,
    routinesOffered && draftHasSkill
  );
  const isPromptEmpty = isProjectsComposerDraftEmpty(draftValue);
  const rotationEligible = !!rotatingPlaceholder && mode !== 'evidence';
  // Hints hold while the user is in the editor; autofocus alone doesn't count.
  const [isEditorEngaged, setIsEditorEngaged] = useState(false);
  const showRotatingPlaceholder =
    rotationEligible &&
    isPromptEmpty &&
    !placeholderPreview &&
    !disabled &&
    !isSubmitting;

  const promptExpanded = promptOverflow?.expanded ?? false;
  const hasPromptOverflow = usePromptOverflow(
    tiptapEditor,
    promptExpanded,
    draftValue,
    promptOverflow !== undefined
  );
  const showExpandToggle =
    promptOverflow?.onToggle !== undefined &&
    (hasPromptOverflow || promptExpanded) &&
    !isDictationActive;

  const {
    showToolbar: showComposerToolbar,
    showInlineControls: showInlineCompactControls,
    containerRef,
    cardRef: composerCardRef,
    cardHandlers,
    engage,
    disengage,
  } = useCollapsedEngagement({
    isCollapsible: isCollapsibleLayout,
    // A live dictation holds the card open: collapsing mid-dictation would
    // remount the dictate control and drop the transcript it is waiting on.
    hasContent:
      !isPromptEmpty ||
      attachmentItems.length > 0 ||
      linkedItems.length > 0 ||
      isDictationActive,
    autoFocus,
    disabled,
    isSubmitting,
    onExpandedChange,
  });
  const isExpanded = showComposerToolbar;

  const handleModeChange = (next: WorkMode) => {
    if (next === 'evidence') {
      const stripped = withoutProjectsComposerStarterBundle(
        stripProjectsComposerSkills(draftRef.current)
      );
      const normalized = normalizeDraftForMode(stripped, false);
      if (!draftsHaveSameContent(normalized, draftRef.current)) {
        editorRef.current?.replaceDraft(normalized);
      }
      if (!draftsHaveSameContent(stripped, draftRef.current)) {
        toast({
          title: intl.formatMessage({
            defaultMessage: "Routines aren't available in Evidence",
            id: 'Y0zecDOHz+',
            description:
              'Toast after removing a routine chip when switching the landing composer to Evidence',
          }),
        });
      }
      if (normalized !== stripped) {
        toast({
          title: intl.formatMessage({
            defaultMessage: "Mentions aren't available in Evidence",
            id: 'EohRLbXfR0',
            description:
              'Toast title after converting @ mentions to plain text when switching the composer to Evidence',
          }),
          description: intl.formatMessage({
            defaultMessage: 'They were kept as text.',
            id: 'Sh94DUfwf6',
            description: 'Toast body: the @ mentions stay in the prompt as plain names',
          }),
        });
      }
    }
    onModeChange?.(next);
    editorRef.current?.focus('end');
  };

  const didAutoFocusRef = useRef(false);

  // The editor's own `autoFocus` prop is applied on mount by the editor
  // implementation; the drawer's Evidence entry needs the caret placed even when
  // the scene remounts around an already-mounted editor, so ask for it directly.
  useEffect(() => {
    if (didAutoFocusRef.current) return;
    if (!autoFocus || disabled || isSubmitting) return;
    didAutoFocusRef.current = true;
    engage();
    focusComposerAlways();
  }, [autoFocus, disabled, engage, focusComposerAlways, isSubmitting]);

  const handleFocusRequest = useCallback(() => {
    if (disabled || isSubmitting) return;
    engage();
    focusComposerAlways();
  }, [disabled, engage, focusComposerAlways, isSubmitting]);
  useRequestEffect(focusRequest, handleFocusRequest);

  useRequestEffect(collapseRequest, disengage);

  const handleLinkContextSet = useCallback(
    (item: LinkedContextItem | null) => {
      setLinkedItem(item);
      onLinkedContextChange?.(item);
      if (item) focusComposerAlways();
    },
    [focusComposerAlways, onLinkedContextChange, setLinkedItem]
  );

  const handleInvokeSkill = (skill: ProjectsComposerSkillReference) => {
    if (mode === 'evidence') onModeChange?.('work');
    if (!isDesktopBridgeAvailable && skill.kind !== 'workflow') {
      toast({
        title: intl.formatMessage(composerSkillToastMessages.title),
        description: intl.formatMessage(composerSkillToastMessages.desktopRequired),
        tone: 'destructive',
      });
      return;
    }
    editorRef.current?.insertSkill(skill);
    editorRef.current?.focus('end');
  };

  const submit = (currentDraft: ProjectsComposerPromptDraft = draftValue) => {
    if (disabled || sendDisabled || sendBlocked) return;
    const submittable = isProjectsComposerSubmittable(
      currentDraft.text,
      routinesOffered && hasProjectsComposerSkill(currentDraft)
    );
    if (!submittable && !(isBoundChat && attachmentItems.length > 0)) return;
    if (!attachments.readyToSend) {
      if (!isBoundChat && !isSubmitting) {
        toast({
          title: intl.formatMessage(composerSkillToastMessages.attachmentsPendingTitle),
          description: intl.formatMessage(
            composerSkillToastMessages.attachmentsPendingBody
          ),
        });
      }
      return;
    }
    onSubmit({
      draft: normalizeDraftForMode(currentDraft, objectTokensEnabled),
      attachments: attachmentItems,
      linkedContext: getDefaultStore().get(composerLinkedItemsAtom),
      runFlags: projectRunFlagsWithSpeed(runFlags, canApplySpeed ? speed : 'normal'),
      approvalMode: approvalMode ?? undefined,
    });
  };

  const sendShortcut = (query: string) => {
    const text = query.trim();
    if (!text) return;
    onShortcutSubmit?.(
      {
        draft: { text, segments: [{ kind: 'text', text }] },
        attachments: [],
        linkedContext: getDefaultStore().get(composerLinkedItemsAtom),
        runFlags: projectRunFlagsWithSpeed(runFlags, canApplySpeed ? speed : 'normal'),
        approvalMode: approvalMode ?? undefined,
      },
      draftRef.current
    );
  };
  const shortcutSend =
    onShortcutSubmit && !disabled && !sendDisabled && !sendBlocked
      ? sendShortcut
      : undefined;

  const hasSendablePayload =
    isPromptSubmittable || (isBoundChat && attachmentItems.length > 0);
  const showStop = stopState !== undefined;
  const showSend = showComposerToolbar && (isSubmitting || hasSendablePayload);
  const sendUnavailable =
    sendBlocked ||
    (!isSubmitting &&
      (!hasSendablePayload || disabled || sendDisabled || !attachments.readyToSend));
  const sendButton = (
    <Button
      size="md"
      iconOnly
      variant={persistentSend ? (showSend ? 'default' : 'ghost') : undefined}
      onClick={() => submit()}
      disabled={sendUnavailable}
      aria-label={
        sendAriaLabel ??
        (isSubmitting
          ? intl.formatMessage(projectChatComposerMessages.sending)
          : intl.formatMessage(projectChatComposerMessages.createProject))
      }
      aria-busy={isSubmitting || undefined}
      data-testid={testIds.send}
      className={cn(
        'transition-transform active:scale-95 motion-reduce:transform-none',
        persistentSend ? 'rounded-full' : 'rounded-lg',
        sendSucceeded &&
          'scale-110 text-emerald-600 transition-transform duration-200 motion-reduce:transition-none'
      )}
    >
      {isSubmitting ? (
        <Loader2 className="size-4 animate-spin" />
      ) : sendSucceeded ? (
        <Check className="size-4" />
      ) : (
        <ArrowUp className="size-4" />
      )}
    </Button>
  );
  const stopButton = stopState ? (
    <Button
      size="md"
      iconOnly
      onClick={stopState.onStop}
      disabled={stopState.disabled}
      aria-label={stopState.ariaLabel}
      data-testid={testIds.stop}
      className={cn(
        'rounded-lg transition-transform active:scale-95',
        stopState.pending && 'opacity-80'
      )}
    >
      {stopState.pending || isSubmitting ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Square className="size-3 fill-current" />
      )}
    </Button>
  ) : null;
  const dictateButton = dictateSlot?.({
    editor: tiptapEditor,
    getEditor: () => editorRef.current?.getEditor() ?? null,
    disabled: disabled || isSubmitting,
    buttonClassName: composerToolbarIconButtonClassName,
    onDictateClick: () =>
      trackCta?.(WORK_HOME_COMPOSER_ENTRY.dictation, {
        surface: analyticsSurface,
      }),
  });
  // Always rendered: the host observes the dictation store through it even
  // while idle, and only paints once a dictation is live.
  const dictationRow = renderDictation?.({
    editor: tiptapEditor,
    getEditor: () => editorRef.current?.getEditor() ?? null,
    onActiveChange: setIsDictationActive,
  });
  const compactLinkedItem = isCollapsibleLayout ? linkedItems[0] : undefined;
  const compactLinkedArtifact = compactLinkedItem
    ? artifactForLinkedItem(compactLinkedItem)
    : null;
  // `useId` scopes the shared layoutId per instance so two composers on one
  // screen don't cross-link and fly into each other; undefined disables the
  // slide for reduced motion and non-collapsible layouts.
  const addMenuInstanceId = useId();
  const enableAddMenuSlide = isCollapsibleLayout && !shouldReduceMotion;
  const addMenuLayoutId = enableAddMenuSlide
    ? `composer-add-menu-${addMenuInstanceId}`
    : undefined;
  // dictate/voice slide like the `+`; only shared in work mode, since evidence
  // mode swaps the dictate slot for send/stop (no collapsed counterpart).
  const enableRightControlSlide = enableAddMenuSlide && mode !== 'evidence';
  // The lift assumes the pill directly follows the `+`.
  const enableEvidencePillSlide =
    enableAddMenuSlide && mode === 'evidence' && !toolbarLeadingSlot;
  const evidencePillLayoutId = enableEvidencePillSlide
    ? `composer-evidence-pill-${addMenuInstanceId}`
    : undefined;
  const dictateLayoutId = enableRightControlSlide
    ? `composer-dictate-${addMenuInstanceId}`
    : undefined;
  const voiceLayoutId = enableRightControlSlide
    ? `composer-voice-${addMenuInstanceId}`
    : undefined;
  const hasStandaloneSourcesControl = Boolean(
    toolbarSourcesSlot || (mode === 'evidence' && sourcesSlot)
  );
  const showIdleSources =
    isCollapsibleLayout && showToolbarSourcesWhileIdle && Boolean(toolbarSourcesSlot);
  const addMenuBind: ComposerAddMenuBind = {
    items: linkedItems,
    onSet: handleLinkContextSet,
    onPickObject: objectTokensEnabled
      ? (item) => {
          const editorHandle = editorRef.current;
          if (!editorHandle) return;
          editorHandle.insertChatObject(item);
          onObjectTokenInserted?.(item);
        }
      : undefined,
    onAfterPick: focusComposerAlways,
    onAttachFiles: () => fileInputRef.current?.click(),
    skillsMenu:
      offeredSkills.length > 0
        ? {
            skills: offeredSkills,
            onInvoke: handleInvokeSkill,
          }
        : undefined,
    speedMenu,
    modeMenu: onModeChange
      ? { value: mode ?? 'work', onChange: handleModeChange }
      : undefined,
    disabled: isSubmitting || disabled,
    analyticsSurface,
    showSourcesInAddMenu:
      (layout === 'condensed' && !toolbarSourcesSlot) ||
      (isCollapsibleLayout &&
        (isExpanded ? !hasStandaloneSourcesControl : !showIdleSources)),
    onShortcutSend: shortcutSend,
  };
  // `slide` adds the shared-element wrappers; false keeps the markup identical to
  // the original toolbar, so condensed/full layouts are unchanged.
  // Evidence has no voice slot, so send takes the dictate slot once there is
  // something to send (the same rule as Work: no send before a prompt).
  // While a dictation is live the stop control moves beside the dictation row
  // (see the block right after `{dictationRow}` below), so this slot falls
  // back to the mic/send state instead of mounting a second stop button here.
  const dictateSlotContent =
    mode === 'evidence' && !isDictationActive
      ? showStop
        ? stopButton
        : showSend && !persistentSend
          ? sendButton
          : dictateButton
      : dictateButton;
  const renderComposerRightControls = ({ slide }: { slide: boolean }) => (
    <div className="flex items-center gap-0.5">
      {slide ? (
        <m.div
          layoutId={dictateLayoutId}
          initial={false}
          transition={motionConfig.addMenuSlide}
          className="flex size-9 shrink-0 items-center justify-center"
        >
          {dictateSlotContent}
        </m.div>
      ) : (
        <div className="flex size-9 shrink-0 items-center justify-center">
          {dictateSlotContent}
        </div>
      )}
      {persistentSend ? (
        <div className="flex size-9 shrink-0 items-center justify-center">
          {sendButton}
        </div>
      ) : (
        <>
          {mode !== 'evidence' && !showStop && !showSend ? (
            slide ? (
              <m.div
                layoutId={voiceLayoutId}
                initial={false}
                transition={motionConfig.addMenuSlide}
                className="flex shrink-0 items-center"
              >
                {voiceTrigger}
              </m.div>
            ) : (
              voiceTrigger
            )
          ) : null}
          <AnimatePresence mode="popLayout" initial={false}>
            {mode !== 'evidence' && !isDictationActive && (showStop || showSend) ? (
              <m.div
                key={showStop ? 'work-stop' : 'work-send'}
                initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={shouldReduceMotion ? undefined : { opacity: 0, scale: 0.85 }}
                transition={motionConfig.toolbarAction}
                className="flex size-9 shrink-0 items-center justify-center"
              >
                {showStop ? stopButton : sendButton}
              </m.div>
            ) : null}
          </AnimatePresence>
        </>
      )}
    </div>
  );
  const composerToolbarRow = (
    <>
      <div className="flex min-w-0 items-center gap-0.5 [&>*]:flex [&>*]:items-center">
        {/* When the `+` slides it's lifted out of the toolbar; this placeholder
          holds its slot. */}
        {enableAddMenuSlide ? (
          <div
            className="size-9 shrink-0"
            aria-hidden
            data-testid="composer-add-menu-placeholder"
          />
        ) : (
          renderAddMenu(addMenuBind)
        )}
        {toolbarLeadingSlot ? <div className="shrink-0">{toolbarLeadingSlot}</div> : null}
        {enableEvidencePillSlide ? (
          // Reserves the lifted pill's localized width so Sources stays put.
          <div className="invisible shrink-0" aria-hidden>
            <EvidenceOnlyPill
              testId="composer-evidence-only-pill-slot"
              disabled
              onClear={() => undefined}
            />
          </div>
        ) : mode === 'evidence' ? (
          <div className="shrink-0">
            <EvidenceOnlyPill
              disabled={disabled || isSubmitting || !onModeChange}
              onClear={() => handleModeChange('work')}
            />
          </div>
        ) : (
          <div className="shrink-0">
            <ProjectApprovalModeSelector
              value={approvalMode ?? 'manual'}
              onChange={handleApprovalModeChange}
              disabled={disabled || isSubmitting}
            />
          </div>
        )}
        {mode === 'evidence' && sourcesSlot ? (
          <div className="shrink-0">{sourcesSlot}</div>
        ) : null}
        {toolbarSourcesSlot ? (
          <div className="shrink-0" data-testid="composer-toolbar-sources">
            {showIdleSources ? (
              <PresenceGuardedSlot>{toolbarSourcesSlot}</PresenceGuardedSlot>
            ) : (
              toolbarSourcesSlot
            )}
          </div>
        ) : null}
        {showRunOptions && mode !== 'evidence' ? (
          <ProjectRunOptionsSelector
            value={runFlags}
            onChange={setRunFlags}
            disabled={isSubmitting || disabled}
          />
        ) : null}
      </div>

      {/* Inline unless it slides (work mode), so it fades with the toolbar; no
        placeholder needed since it's the rightmost element. */}
      {enableRightControlSlide ? null : renderComposerRightControls({ slide: false })}
    </>
  );

  const showLinkedChips =
    (host.kind !== 'project' || host.lock === null) && linkedItems.length > 0;
  const hasAttachedRow =
    (!isCollapsibleLayout && showLinkedChips) || attachmentItems.length > 0;
  const isGlowVisible =
    glow !== undefined && !disabled && (glow !== 'while-generating' || isGenerating);
  const hasSquareBottomEdge = squareBottomEdge || isCardTrailingVisible;
  // 20px is off the radius scale (xl=16, 2xl=24); animate on the
  // compact↔full spring so the corners ease with the expand. The inline radius
  // beats any `rounded-*` class, so a joined trailing sheet squares the bottom
  // corners here rather than through Tailwind — instantly, because the sheet
  // appears at once and a still-rounding corner would notch the joined outline.
  const cardRadius = showInlineCompactControls ? 20 : 24;
  const cardBottomRadius = hasSquareBottomEdge ? 0 : cardRadius;
  const cardBottomRadiusTransition = hasSquareBottomEdge
    ? { duration: 0 }
    : motionConfig.compactLayout;
  const cardPaddingY = showInlineCompactControls
    ? COMPACT_CARD_PADDING_Y
    : EXPANDED_CARD_PADDING_Y;

  const composerCard = (
    <m.div
      ref={composerCardRef}
      data-testid={testIds.card}
      layout={isCollapsibleLayout && !shouldReduceMotion ? 'position' : false}
      initial={false}
      animate={
        isCollapsibleLayout
          ? {
              borderTopLeftRadius: cardRadius,
              borderTopRightRadius: cardRadius,
              borderBottomLeftRadius: cardBottomRadius,
              borderBottomRightRadius: cardBottomRadius,
              paddingTop: cardPaddingY,
              paddingBottom: cardPaddingY,
            }
          : undefined
      }
      transition={{
        layout: motionConfig.compactLayout,
        borderTopLeftRadius: motionConfig.compactLayout,
        borderTopRightRadius: motionConfig.compactLayout,
        borderBottomLeftRadius: cardBottomRadiusTransition,
        borderBottomRightRadius: cardBottomRadiusTransition,
        paddingTop: motionConfig.compactLayout,
        paddingBottom: motionConfig.compactLayout,
      }}
      className={cn(
        // Chat's suggestion sheet reads this group to share the card's active
        // border token. Declaring it on the package-owned host keeps the sheet
        // app-side while the joined outline stays one colour.
        'group/chatinput relative z-20 flex min-w-0 flex-col border border-border bg-surface motion-reduce:transition-none',
        isCollapsibleLayout
          ? cn(
              'group/landing w-full max-w-full flex-1 transition-[border-color,box-shadow] duration-200 ease-out',
              elevated ? 'shadow-chat' : 'shadow-none',
              'gap-2 rounded-[20px] px-3',
              'hover:border-border-hover',
              isDragOver && 'border-border-focus'
            )
          : cn(
              'gap-2 px-3 pb-2.5 pt-4 transition-all duration-200 ease-out',
              'min-w-0 flex-1',
              hasSquareBottomEdge ? 'rounded-b-none rounded-t-2xl' : 'rounded-2xl',
              elevated && 'shadow-chat',
              !disabled && 'hover:border-border-hover',
              isDragOver && 'border-border-strong'
            ),
        'focus-within:border-border-pressed',
        isCardTrailingVisible && 'border-border-pressed hover:border-border-pressed',
        disabled && !disabledMessage && 'opacity-70'
      )}
      onMouseDown={(event) => handleComposerShellClick(event, focusComposerAlways)}
      onFocusCapture={cardHandlers.onFocusCapture}
      onBlur={cardHandlers.onBlur}
      // When the host owns a full-surface takeover, the card must not intercept
      // (or stopPropagation) the drag, or the window/panel listener never sees it.
      {...(disableFileDrop ? {} : dropHandlers)}
    >
      {!disableFileDrop && (
        <ComposerFileDropOverlay visible={isDragOver} testId={testIds.dropOverlay} />
      )}
      {showExpandToggle && promptOverflow?.onToggle ? (
        <PromptExpandToggle
          expanded={promptOverflow.expanded}
          onToggle={promptOverflow.onToggle}
        />
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={attachments.accept}
        aria-label={intl.formatMessage(projectChatComposerMessages.attachFiles)}
        className="hidden"
        data-testid={testIds.fileInput}
        onChange={(e) => {
          attachFiles(e.target.files);
          e.target.value = '';
        }}
        disabled={disabled || isSubmitting}
      />

      {hasAttachedRow ? (
        <div
          className={cn(
            'flex flex-col gap-2',
            // Match the editor row so attached items share the text's left edge.
            (!isCollapsibleLayout || !showInlineCompactControls) && 'ps-3'
          )}
        >
          {!isCollapsibleLayout && showLinkedChips ? (
            <div
              className="flex flex-wrap items-center gap-2"
              data-testid={testIds.linkedChips}
            >
              {contextCardSlot ??
                linkedItems.map((item) => {
                  const key = linkedContextItemKey(item);
                  const artifact = artifactForLinkedItem(item);
                  return (
                    <span key={key}>
                      {linkedContextBadge?.(item, {
                        onOpen: artifact ? () => setOpenArtifact(artifact) : undefined,
                        onRemove: removeLinkedItem,
                      }) ?? linkedContextItemLabel(item)}
                    </span>
                  );
                })}
            </div>
          ) : null}
          {attachmentItems.length > 0 ? (
            <AttachmentRow
              items={attachmentItems}
              onRemove={attachments.remove}
              onRetry={attachments.retry}
              renderImageItem={attachments.renderImageItem}
              testId={testIds.attachments}
            />
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          'relative flex min-h-6 min-w-0',
          isCollapsibleLayout
            ? 'items-center [&>[data-compact-composer-control]]:-my-2'
            : 'ps-3',
          // Line the text up with the `+` glyph, not its button box (already at the card edge).
          isCollapsibleLayout && !showInlineCompactControls && 'ps-3',
          showInlineCompactControls && 'gap-1',
          showExpandToggle && 'pe-12'
        )}
      >
        {showInlineCompactControls ? (
          <m.div
            key="compact-link-context"
            layoutId={addMenuLayoutId}
            initial={false}
            transition={motionConfig.addMenuSlide}
            className="shrink-0"
            data-compact-composer-control
            onClick={(event) => event.stopPropagation()}
          >
            {renderAddMenu(addMenuBind)}
          </m.div>
        ) : null}
        {/* Evidence-only survives blur, so the collapsed row has to say so —
            otherwise clicking out of an evidence composer leaves the page with
            no indication of the mode it is still in. Marked as a control so
            dismissing it changes the CONTENT without engaging the composer. */}
        {showInlineCompactControls && mode === 'evidence' ? (
          // `me-2` on top of the row's `gap-1`: the placeholder read as
          // crowded against the chip at the row gap alone. Scoped here so the
          // default collapsed row keeps its spacing.
          <m.div
            key="compact-evidence-pill"
            layoutId={evidencePillLayoutId}
            initial={false}
            transition={motionConfig.addMenuSlide}
            className="me-2 shrink-0"
            data-compact-composer-control
            onClick={(event) => event.stopPropagation()}
          >
            <EvidenceOnlyPill
              testId="composer-collapsed-evidence-chip"
              disabled={disabled || isSubmitting || !onModeChange}
              onClear={() => onModeChange?.('work')}
            />
          </m.div>
        ) : null}
        {dictationRow}
        {/* Dictating no longer takes the run-stop control with it: the toolbar
          (and its own stop button) is hidden while a dictation is live, so a
          streaming run would otherwise be stoppable only via Esc. Replying by
          voice mid-run is the point of this surface, so the fix keeps stop
          reachable here rather than disabling dictation during a run. */}
        {isDictationActive && showStop ? (
          <div className="flex shrink-0 items-center self-center">{stopButton}</div>
        ) : null}
        {/* The `+` leaving this row shifts the text's left edge; slide it instead
          of snapping. layoutDependency scopes the re-measure to the expand flip
          and mode, so typing doesn't trigger it. Hidden, not unmounted, while a
          dictation is live: the transcript inserts into this editor when it
          completes. */}
        <m.div
          layout={isCollapsibleLayout && !shouldReduceMotion ? 'position' : false}
          layoutDependency={`${showInlineCompactControls}:${mode}`}
          transition={{ layout: motionConfig.compactLayout }}
          className={cn('relative min-w-0 flex-1', isDictationActive && 'hidden')}
          onPointerDownCapture={() => setIsEditorEngaged(true)}
          onKeyDownCapture={() => setIsEditorEngaged(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setIsEditorEngaged(false);
            }
          }}
        >
          {renderPromptEditor({
            editorRef,
            anchorRef: composerCardRef,
            availableSkills: offeredSkills,
            autoFocus: (!isCollapsibleLayout || autoFocus) && !disabled && !isSubmitting,
            placeholder: rotationEligible
              ? intl.formatMessage(projectChatComposerMessages.placeholderCompact)
              : (placeholder ??
                intl.formatMessage(
                  mode === 'evidence'
                    ? projectChatComposerMessages.placeholderEvidence
                    : projectChatComposerMessages.placeholder,
                  PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES
                )),
            disabled: disabled || isSubmitting,
            prefilledValue,
            prefilledSkill: prefilledDesktopSkill,
            placeholderPreview: isPromptEmpty ? (placeholderPreview ?? null) : null,
            placeholderOverlay:
              showRotatingPlaceholder && rotatingPlaceholder ? (
                <RotatingComposerPlaceholder
                  lines={rotatingPlaceholder}
                  paused={isEditorEngaged}
                />
              ) : null,
            onDraftChange: handleDraftChange,
            onSubmitRequest: (next) => submit(next ?? draftValue),
            onShortcutSend: shortcutSend,
            onLinkContext: handleLinkContextSet,
            onOpenArtifact: setOpenArtifact,
            objectTokens: objectTokensEnabled,
            onClipboardPaste: handlePaste,
            onEditorChange: setTiptapEditor,
            analyticsSurface,
            className: 'relative min-w-0',
            editorClassName: isCollapsibleLayout
              ? 'bg-transparent max-h-32 min-w-0 overflow-y-auto overflow-x-hidden py-1'
              : cn(
                  'scrollbar-fade overflow-y-auto overscroll-contain bg-transparent',
                  promptExpanded ? 'h-[min(58dvh,44rem)]' : 'max-h-[200px]'
                ),
            testId: testIds.input,
          })}
        </m.div>
        {/* Idle Sources sits just before the mic; it crossfades with the
          toolbar copy (which fades in and out with the toolbar) rather than moving. */}
        <AnimatePresence initial={false} mode="popLayout">
          {showInlineCompactControls && showIdleSources ? (
            <m.div
              key="compact-sources"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={motionConfig.sourcesFade}
              className="shrink-0"
              data-compact-composer-control
              data-testid="composer-collapsed-sources"
              onClick={(event) => event.stopPropagation()}
            >
              <PresenceGuardedSlot>{toolbarSourcesSlot}</PresenceGuardedSlot>
            </m.div>
          ) : null}
        </AnimatePresence>
        {enableRightControlSlide ? (
          showInlineCompactControls ? (
            <div
              className="flex shrink-0 items-center gap-0.5"
              data-compact-composer-control
              onClick={(event) => event.stopPropagation()}
            >
              <m.div
                layoutId={dictateLayoutId}
                initial={false}
                transition={motionConfig.addMenuSlide}
                className="flex size-9 shrink-0 items-center justify-center"
              >
                {dictateButton}
              </m.div>
              {/* mode is 'work' here (the slide gate excludes evidence). */}
              <m.div
                layoutId={voiceLayoutId}
                initial={false}
                transition={motionConfig.addMenuSlide}
                className="flex shrink-0 items-center"
              >
                {voiceTrigger}
              </m.div>
            </div>
          ) : null
        ) : (
          // Evidence/reduced-motion don't slide, so keep the original fade in/out.
          <AnimatePresence initial={false} mode="popLayout">
            {showInlineCompactControls ? (
              <m.div
                key="compact-inline-controls"
                initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.95 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: motionConfig.compactInlineControlEnter,
                }}
                exit={
                  shouldReduceMotion
                    ? undefined
                    : {
                        opacity: 0,
                        scale: 0.95,
                        transition: motionConfig.compactInlineControlExit,
                      }
                }
                className="flex shrink-0 items-center gap-0.5 overflow-hidden"
                data-compact-composer-control
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex size-9 shrink-0 items-center justify-center">
                  {dictateButton}
                </div>
                {mode !== 'evidence' ? voiceTrigger : null}
              </m.div>
            ) : null}
          </AnimatePresence>
        )}
      </div>

      {enableAddMenuSlide && showComposerToolbar ? (
        // Rendered outside the overflow-hidden height shell and the transforming
        // toolbar, which would clip and mis-project the shared-element slide.
        // Mirrors the toolbar's left cluster so the pill lands on its slot.
        <div
          className={cn(
            LIFTED_CONTROL_CLASS,
            'start-3 flex items-center gap-0.5',
            isDictationActive && 'hidden'
          )}
          data-testid="composer-lifted-leading-controls"
        >
          <m.div
            layoutId={addMenuLayoutId}
            initial={false}
            transition={motionConfig.addMenuSlide}
            className="shrink-0"
            data-testid="composer-lifted-add-menu"
          >
            {renderAddMenu(addMenuBind)}
          </m.div>
          {enableEvidencePillSlide ? (
            <m.div
              layoutId={evidencePillLayoutId}
              initial={false}
              transition={motionConfig.addMenuSlide}
              className="shrink-0"
            >
              <EvidenceOnlyPill
                disabled={disabled || isSubmitting || !onModeChange}
                onClear={() => handleModeChange('work')}
              />
            </m.div>
          ) : null}
        </div>
      ) : null}

      {isCollapsibleLayout ? (
        <m.div
          initial={false}
          // Collapsed to 0 while a dictation is live: the toolbar inside is
          // `hidden`. The open target is the row's own measured height, not the
          // string `'auto'` — see `toolbarContentHeight` above for why.
          animate={{
            height:
              showComposerToolbar && !isDictationActive
                ? (toolbarContentHeight ?? 'auto')
                : 0,
            marginTop: showComposerToolbar && !isDictationActive ? 0 : -8,
          }}
          transition={motionConfig.compactToolbarShell}
          // Bottom-aligned and unclipped: the toolbar holds its final row beside
          // the lifted `+` and dictate controls while the shell opens above it,
          // and its fade is timed to the text line clearing that row.
          className="flex flex-col justify-end"
          data-testid="composer-toolbar-shell"
        >
          <AnimatePresence initial={false}>
            {showComposerToolbar ? (
              <m.div
                key="composer-toolbar"
                ref={setToolbarContentNode}
                initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={
                  shouldReduceMotion
                    ? undefined
                    : {
                        opacity: 0,
                        scale: 0.995,
                        transition: motionConfig.compactToolbarContentExit,
                      }
                }
                transition={motionConfig.compactToolbarContent}
                className={cn(
                  'flex min-h-10 items-center justify-between gap-2',
                  isDictationActive && 'hidden'
                )}
                data-testid="composer-toolbar"
              >
                {composerToolbarRow}
              </m.div>
            ) : null}
          </AnimatePresence>
        </m.div>
      ) : showComposerToolbar ? (
        // Hidden, not unmounted, while a dictation is live: the dictate control
        // in here owns the session and inserts the transcript on completion.
        <div
          className={cn(
            'flex min-h-10 items-center justify-between gap-2',
            isDictationActive && 'hidden'
          )}
          data-testid="composer-toolbar"
        >
          {composerToolbarRow}
        </div>
      ) : null}

      {/* After the toolbar shell in the DOM so tab/reading order matches the
        visual order. */}
      {enableRightControlSlide && showComposerToolbar ? (
        <div
          className={cn(LIFTED_CONTROL_CLASS, 'end-3', isDictationActive && 'hidden')}
          data-testid="composer-lifted-right-controls"
        >
          {renderComposerRightControls({ slide: true })}
        </div>
      ) : null}

      {disabled && disabledMessage ? (
        <div
          data-testid="projects-landing-composer-disabled-message"
          className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-2xl bg-background-secondary/70 px-4 text-center"
        >
          <span className="text-sm font-medium text-text-secondary">
            {disabledMessage}
          </span>
        </div>
      ) : null}
      {cardTrailingSlot}
      {renderCardTrailing?.({
        editor: tiptapEditor,
        onVisibilityChange: setIsCardTrailingVisible,
      })}
    </m.div>
  );

  const composerRow = (
    <m.div
      layout={isCollapsibleLayout && !shouldReduceMotion ? 'position' : false}
      transition={{ layout: motionConfig.compactLayout }}
      className={cn(
        'relative flex w-full min-w-0 max-w-full items-end gap-3',
        compactLinkedItem && 'pt-12'
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {compactLinkedItem ? (
          <m.div
            key={linkedContextItemKey(compactLinkedItem)}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 12, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0, y: 10, scale: 0.995 }}
            transition={motionConfig.compactLayout}
            className="absolute inset-x-0 top-0 z-10 w-full"
          >
            {contextCardSlot ?? (
              <WorkHomeAttachedContext
                item={compactLinkedItem}
                onRemove={removeLinkedItem}
                onSelectPatient={(patient) => handleLinkContextSet(patient)}
                onOpen={
                  compactLinkedArtifact
                    ? () => setOpenArtifact(compactLinkedArtifact)
                    : undefined
                }
              />
            )}
          </m.div>
        ) : null}
      </AnimatePresence>

      {isCollapsibleLayout || glow === undefined ? (
        composerCard
      ) : (
        <div className="group/landing relative min-w-0 flex-1">
          {glow === 'while-generating' && host.kind === 'project' ? (
            <ComposerAmbientGlow
              visible={isGlowVisible && !promptExpanded}
              testId={testIds.glow}
            />
          ) : (
            <div
              aria-hidden
              data-testid={testIds.glow}
              className={cn(
                workLandingComposerGlowClassName,
                (promptExpanded || !isGlowVisible) && 'opacity-0',
                isDragOver && 'bg-accent-yellow/25'
              )}
            />
          )}
          {composerCard}
        </div>
      )}
    </m.div>
  );

  return (
    <div
      ref={containerRef}
      data-work-home-composer-cluster
      className={cn(
        'flex w-full min-w-0 max-w-full flex-col items-center gap-3',
        className
      )}
    >
      {consumesPendingLinkedContext ? (
        <PendingLinkedContextConsumer
          editor={tiptapEditor}
          editorRef={editorRef}
          objectTokensEnabled={objectTokensEnabled}
          onObjectTokenInserted={onObjectTokenInserted}
          setOpenArtifact={setOpenArtifact}
        />
      ) : null}
      {composerDockLeading}
      {desktopTargetSlot}
      {bannerSlot === undefined ? (
        composerRow
      ) : (
        <div className="relative flex w-full min-w-0 flex-col">
          {bannerSlot}
          {composerRow}
        </div>
      )}
    </div>
  );
};

// ONE size, both composer heights: the collapsed row already stands 36px tall
// for its icon buttons, so the full pill fits it without growing the card.
function EvidenceOnlyPill({
  disabled,
  onClear,
  testId = 'composer-evidence-only-pill',
}: {
  disabled: boolean;
  onClear: () => void;
  testId?: string;
}) {
  const intl = useIntl();
  return (
    <span
      data-testid={testId}
      className="inline-flex h-9 items-center gap-1 rounded-full bg-entity-blue-muted py-0 pe-1 ps-3 text-entity-blue-foreground"
    >
      <span className="text-sm font-medium">
        {intl.formatMessage({
          defaultMessage: 'Evidence only',
          id: 'R+pSFnIHL4',
          description: 'Composer add-menu switch that restricts the prompt to Evidence',
        })}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="xxs"
        iconOnly
        disabled={disabled}
        aria-label={intl.formatMessage({
          defaultMessage: 'Turn off Evidence only',
          id: 'rhK1AwoDp9',
          description: 'Clears the Evidence-only composer pill and returns to Ask',
        })}
        onClick={onClear}
        className="text-entity-blue-foreground hover:bg-transparent hover:text-entity-blue-foreground"
      >
        <X size={14} aria-hidden />
      </Button>
    </span>
  );
}
